import React, { useCallback, useEffect, useRef, useState } from "react";
import PortDetailPanel from "./PortDetailPanel";

function SignUpPrompt() {
  return (
    <div className="parts-list-panel-signup">
      <p className="parts-list-panel-signup-text">
        See a full breakdown of devices, cables, and power requirements for your rig.
      </p>
      <a href="/users/sign_up" className="parts-list-panel-signup-link">
        Sign up to view your parts list
      </a>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="parts-list-panel-empty">
      Add devices to your rig to see your parts list.
    </div>
  );
}

function NoRigState() {
  return (
    <div className="parts-list-panel-empty">
      No rig selected.
    </div>
  );
}

function findTopLeftNode(nodes, type, matchFn) {
  const matching = nodes.filter((n) => n.type === type && matchFn(n));
  if (matching.length === 0) return null;
  matching.sort((a, b) => {
    const dy = a.position.y - b.position.y;
    return dy !== 0 ? dy : a.position.x - b.position.x;
  });
  return matching[0].id;
}

function DeviceItem({ item, selected, onSelect, nodes }) {
  const ref = useRef(null);
  const labels = item.labels || [];

  useEffect(() => {
    if (selected && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selected]);

  const handleClick = () => {
    const nodeId = findTopLeftNode(nodes, "device", (n) => {
      const d = n.data?.device;
      if (!d) return false;
      if (item.device_system) {
        return d.device_system && d.device_system.split(", ").includes(item.device_system);
      }
      return d.manufacturer === item.manufacturer && d.name === item.name;
    });
    if (nodeId) onSelect(nodeId);
  };

  return (
    <li
      ref={ref}
      className={`parts-list-panel-item${selected ? " parts-list-panel-item--selected" : ""}`}
      onClick={handleClick}
    >
      <div className="parts-list-panel-item-name">
        <span>
          {item.display_name}
          {item.slug && (
            <>
              {" "}
              <a className="parts-list-panel-item-info" href={`/devices/${item.slug}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} aria-label={`View ${item.display_name} details`} title={item.description || "View details"}>
                &#9432;
              </a>
            </>
          )}
        </span>
        {item.quantity > 1 && <span className="parts-list-panel-quantity">x{item.quantity}</span>}
      </div>
      {labels.length > 0 && (
        <div className="parts-list-panel-device-label">{labels.join(", ")}</div>
      )}
    </li>
  );
}

function itemKey(item) {
  return item.device_system ? `system-${item.device_system}` : `${item.manufacturer}-${item.name}`;
}

function nodeMatchesItem(node, item) {
  const d = node?.data?.device;
  if (!d) return false;
  if (item.device_system) {
    return d.device_system && d.device_system.split(", ").includes(item.device_system);
  }
  return d.manufacturer === item.manufacturer && d.name === item.name;
}

function DevicesSection({ devices, nodes, selectedDeviceId, selectedPortId, onSelectDevice, onUpdateExcludedPort }) {
  const selectedNode = nodes.find((n) => n.id === selectedDeviceId);

  if (devices.length === 0) return null;

  return (
    <>
      <div className="parts-list-panel-section-title">Devices</div>
      {devices.map((group) => (
        <div key={group.type_name} className="parts-list-panel-group">
          <div className="parts-list-panel-group-title">{group.type_name}</div>
          <ul className="parts-list-panel-list">
            {group.items.map((item) => {
              const key = itemKey(item);
              const isSelected = selectedNode ? nodeMatchesItem(selectedNode, item) : false;
              return (
                <React.Fragment key={key}>
                  <DeviceItem
                    item={item}
                    selected={isSelected}
                    onSelect={onSelectDevice}
                    nodes={nodes}
                  />
                  {isSelected && selectedPortId && selectedNode && onUpdateExcludedPort && (
                    <li className="parts-list-panel-port-detail">
                      <PortDetailPanel
                        node={selectedNode}
                        portId={selectedPortId}
                        onUpdateExclusion={onUpdateExcludedPort}
                      />
                    </li>
                  )}
                </React.Fragment>
              );
            })}
          </ul>
        </div>
      ))}
    </>
  );
}

function CableItem({ cable, selected, onSelect }) {
  const ref = useRef(null);
  const [showAlternative, setShowAlternative] = useState(false);
  const hasAlternatives = cable.alternatives && cable.alternatives.length > 0;

  useEffect(() => {
    if (selected && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selected]);

  if (showAlternative && hasAlternatives) {
    const alt = cable.alternatives[0];
    return (
      <li ref={ref} className={`parts-list-panel-item parts-list-panel-cable-item${selected ? " parts-list-panel-item--selected" : ""}`} onClick={() => onSelect && onSelect(cable.name)}>
        <div className="parts-list-panel-cable-alt">
          <div className="parts-list-panel-cable-alt-items">
            {alt.items.map((item) => (
              <span key={item.name} className="parts-list-panel-cable-alt-item">
                {item.name}
              </span>
            ))}
          </div>
          <span className="parts-list-panel-cable-count">x{cable.quantity}</span>
        </div>
        <button
          className="parts-list-panel-cable-toggle"
          onClick={(e) => { e.stopPropagation(); setShowAlternative(false); }}
          aria-label="Use adapter cable"
        >
          Use adapter cable
        </button>
      </li>
    );
  }

  return (
    <li ref={ref} className={`parts-list-panel-item parts-list-panel-cable-item${selected ? " parts-list-panel-item--selected" : ""}`} onClick={() => onSelect && onSelect(cable.name)}>
      <div className="parts-list-panel-cable-default">
        <span className="parts-list-panel-cable-type">{cable.name}</span>
        <span className="parts-list-panel-cable-count">x{cable.quantity}</span>
      </div>
      {hasAlternatives && (
        <button
          className="parts-list-panel-cable-toggle"
          onClick={(e) => { e.stopPropagation(); setShowAlternative(true); }}
          aria-label="Use cable + adapter"
        >
          Use cable + adapter
        </button>
      )}
    </li>
  );
}

function CablesSection({ cables, selectedCableName, onSelectCable }) {
  if (cables.length === 0) return null;

  return (
    <>
      <div className="parts-list-panel-section-title">Cables &amp; Adapters</div>
      <ul className="parts-list-panel-list">
        {cables.map((cable) => (
          <CableItem key={cable.name} cable={cable} selected={cable.name === selectedCableName} onSelect={onSelectCable} />
        ))}
      </ul>
    </>
  );
}

const CABLE_TYPE_LABELS = {
  standard: "Standard",
  polarity_reverser: "Polarity Reverser",
  current_doubler: "Current Doubler",
  voltage_doubler: "Voltage Doubler",
  voltage_converter: "Voltage Converter",
  splitter: "Splitter",
};

function PowerCableItem({ cable, selected, onSelect }) {
  const ref = useRef(null);

  useEffect(() => {
    if (selected && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selected]);

  return (
    <li
      ref={ref}
      className={`parts-list-panel-item${selected ? " parts-list-panel-item--selected" : ""}`}
      onClick={() => onSelect && onSelect(cable.name)}
    >
      <span className="parts-list-panel-cable-type">
        {cable.display_name}
        {cable.cable_type !== "standard" && (
          <span className="parts-list-panel-cable-detail">
            {" "}({CABLE_TYPE_LABELS[cable.cable_type] || cable.cable_type})
          </span>
        )}
      </span>
      <span className="parts-list-panel-cable-count">x{cable.quantity}</span>
    </li>
  );
}

function PowerCablesSection({ powerCables, selectedCableName, onSelectCable }) {
  if (!powerCables || powerCables.length === 0) return null;

  return (
    <>
      <div className="parts-list-panel-section-title">Power Cables</div>
      <ul className="parts-list-panel-list">
        {powerCables.map((cable) => (
          <PowerCableItem key={`${cable.manufacturer}-${cable.name}`} cable={cable} selected={cable.name === selectedCableName} onSelect={onSelectCable} />
        ))}
      </ul>
    </>
  );
}

function BoundaryItem({ boundary, direction, selected, nodes, onSelect }) {
  const ref = useRef(null);

  useEffect(() => {
    if (selected && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selected]);

  const handleClick = () => {
    if (!onSelect) return;
    const nodeId = findTopLeftNode(nodes, "signalBoundary", (n) => {
      const b = n.data?.boundary;
      return b && b.label === boundary.label && b.direction === direction;
    });
    if (nodeId) onSelect(nodeId);
  };

  return (
    <li
      ref={ref}
      className={`parts-list-panel-item parts-list-panel-boundary-item${selected ? " parts-list-panel-item--selected" : ""}`}
      onClick={handleClick}
    >
      <div className="parts-list-panel-boundary-label">
        {boundary.label}
        {boundary.quantity > 1 && <span className="parts-list-panel-quantity"> x{boundary.quantity}</span>}
      </div>
      <div className="parts-list-panel-boundary-details">
        {[boundary.connector, boundary.signal_level, boundary.phantom_power].filter(Boolean).join(" \u00b7 ")}
      </div>
    </li>
  );
}

function SignalBoundariesSection({ boundaries, nodes, selectedBoundaryId, onSelectBoundary }) {
  if (!boundaries) return null;
  const { outputs, inputs } = boundaries;
  if ((!outputs || outputs.length === 0) && (!inputs || inputs.length === 0)) return null;

  const selectedNode = nodes.find((n) => n.id === selectedBoundaryId);
  const selectedLabel = selectedNode ? selectedNode.data?.boundary?.label : null;
  const selectedDirection = selectedNode ? selectedNode.data?.boundary?.direction : null;

  return (
    <>
      <div className="parts-list-panel-section-title">Signal I/O</div>
      {outputs && outputs.length > 0 && (
        <div className="parts-list-panel-group">
          <div className="parts-list-panel-group-title">Outputs</div>
          <ul className="parts-list-panel-list">
            {outputs.map((b) => (
              <BoundaryItem
                key={b.label}
                boundary={b}
                direction="output"
                selected={selectedDirection === "output" && selectedLabel === b.label}
                nodes={nodes}
                onSelect={onSelectBoundary}
              />
            ))}
          </ul>
        </div>
      )}
      {inputs && inputs.length > 0 && (
        <div className="parts-list-panel-group">
          <div className="parts-list-panel-group-title">Inputs</div>
          <ul className="parts-list-panel-list">
            {inputs.map((b) => (
              <BoundaryItem
                key={b.label}
                boundary={b}
                direction="input"
                selected={selectedDirection === "input" && selectedLabel === b.label}
                nodes={nodes}
                onSelect={onSelectBoundary}
              />
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

export default function PartsListPanel({ authenticated, currentRigId, csrfToken, saveStatus, nodes = [], edges = [], selectedDeviceId, selectedBoundaryId, selectedEdgeId, selectedPortId, onSelectDevice, onSelectBoundary, onSelectEdge, onUpdateExcludedPort, initialPartsList }) {
  const [partsList, setPartsList] = useState(initialPartsList || null);
  const [loading, setLoading] = useState(
    () => !initialPartsList && !!(authenticated && currentRigId && csrfToken)
  );

  useEffect(() => {
    if (initialPartsList) return;
    if (!authenticated || !currentRigId || !csrfToken) return;

    let cancelled = false;
    fetch(`/api/rigs/${currentRigId}/parts_list`, {
      credentials: "same-origin",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken,
      },
    })
      .then((res) => res.json())
      .then((data) => { if (!cancelled) setPartsList(data); })
      .catch(() => { if (!cancelled) setPartsList(null); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [initialPartsList, authenticated, currentRigId, csrfToken, saveStatus]);

  const selectedEdge = selectedEdgeId ? edges.find((e) => e.id === selectedEdgeId) : null;
  const selectedCableName = selectedEdge?.data?.cableChain?.[0]?.cable?.name || null;

  const handleSelectCable = useCallback((cableName) => {
    if (!onSelectEdge) return;
    const match = edges.find((e) => e.data?.cableChain?.[0]?.cable?.name === cableName);
    if (match) onSelectEdge(match.id);
  }, [edges, onSelectEdge]);

  if (!initialPartsList) {
    if (!authenticated) return <SignUpPrompt />;
    if (!currentRigId) return <NoRigState />;
  }

  if (loading) {
    return <div className="parts-list-panel-loading">Loading parts list...</div>;
  }

  if (!partsList) return null;

  const hasBoundaries = partsList.signal_boundaries && (partsList.signal_boundaries.outputs?.length > 0 || partsList.signal_boundaries.inputs?.length > 0);
  const isEmpty = partsList.devices.length === 0 && partsList.cables.length === 0 && (!partsList.power_cables || partsList.power_cables.length === 0) && !hasBoundaries;

  if (isEmpty) {
    return <EmptyState />;
  }

  return (
    <div className="parts-list-panel">
      {currentRigId && !initialPartsList && (
        <div className="parts-list-panel-download">
          <a
            href={`/api/rigs/${currentRigId}/parts_list_pdf`}
            className="parts-list-panel-download-link"
            download
          >
            Parts PDF
          </a>
        </div>
      )}
      <DevicesSection devices={partsList.devices} nodes={nodes} selectedDeviceId={selectedDeviceId} selectedPortId={selectedPortId} onSelectDevice={onSelectDevice} onUpdateExcludedPort={onUpdateExcludedPort} />
      <SignalBoundariesSection boundaries={partsList.signal_boundaries} nodes={nodes} selectedBoundaryId={selectedBoundaryId} onSelectBoundary={onSelectBoundary} />
      <CablesSection cables={partsList.cables} selectedCableName={selectedCableName} onSelectCable={handleSelectCable} />
      <PowerCablesSection powerCables={partsList.power_cables} selectedCableName={selectedCableName} onSelectCable={handleSelectCable} />
    </div>
  );
}
